/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, withInit } from '@reatom/framework'
import * as DateF from 'date-fns'

import { Socials } from '@root/entities/socials'
import { environmentAtom, TOKEN_BANNERS_HOST } from '@root/shared/environment'
import { reatomDate, reatomDateComputed } from '@root/shared/lib'

import { PremiumMeta } from '../model'
import { PremiumId } from '../model/premium-id'
import * as PremiumOwner from '../model/premium-owner'

import type { PremiumStatus } from '../model/premium-status'
import type { PremiumTier } from '../model/premium-tier'
import type { CurrencyModel } from '@root/entities/currency/model'
import type { ServicesApi } from '@root/shared/api'

const EXTENSION_MIN_EXPIRY_DAYS = 7

class PremiumMetaModelClass {}

export const reatomPremiumMeta = ({
  dto,
  currency,
}: {
  dto: ServicesApi.PremiumMetaDto
  currency: CurrencyModel
}) => {
  //#region Static
  //#endregion

  //#region State
  const premiumIdAtom = atom<PremiumId | undefined>(
    dto.premiumId ? PremiumId(dto.premiumId) : undefined,
  )
  const premiumAtom = atom(dto.premium)
  const boostersAtom = atom(dto.boosters)
  const splashPointsAtom = atom(dto.splashPoints ?? 0)
  const tierAtom = atom(tierFromDto(dto))
  const ownerAtom = atom<PremiumOwner.PremiumOwner | undefined>(undefined).pipe(
    withInit((ctx) => {
      const {
        assets: { avatarsHost },
      } = ctx.get(environmentAtom)
      return PremiumOwner.fromDto(dto, avatarsHost)
    }),
  )
  const expiresAtAtom = reatomDate(new Date(dto.expiresAt))
  const trendingEmojiAtom = atom(
    dto.trendingEmojiIds?.animated
      ? {
          emojiId: dto.trendingEmojiIds.animated,
        }
      : undefined,
  )
  const purchaseTxHashAtom = atom(dto.purchaseTxHash)
  const socialsAtom = atom(
    dto.socials ? Socials.fromDto(dto.socials) : Socials.empty(),
  )
  const updatedAtAtom = reatomDate(new Date(dto.updatedAt))
  const bannerAtom = atom(dto.banner ? bannerFromDto(dto) : undefined)
  const archivedPremiumsAtom = atom<Array<PremiumMeta.ArchivedPremium>>(
    [],
  ).pipe(
    withInit((ctx) => {
      const {
        assets: { avatarsHost, tokenLogosHost, tokenBannersHost },
      } = ctx.get(environmentAtom)

      return dto.archivedPremiums
        ? dto.archivedPremiums.map((dto) =>
            PremiumMeta.archivedPremiumFromDto(dto, {
              avatarsHost,
              tokenLogosHost,
              tokenBannersHost,
            }),
          )
        : []
    }),
  )
  const statusAtom = atom<PremiumStatus>(dto.status)
  //#endregion

  //#region Computed
  const minExtensionDateAtom = reatomDateComputed((ctx) => {
    const expiresAt = ctx.spy(expiresAtAtom)
    return DateF.subDays(expiresAt, EXTENSION_MIN_EXPIRY_DAYS)
  })
  const isAllowedToExtendAtom = atom((ctx) => {
    return (
      ctx.spy(expiresAtAtom.isFuture) && ctx.spy(minExtensionDateAtom.isPast)
    )
  })
  //#endregion

  const updateFromDtoAction = action((ctx, dto: ServicesApi.PremiumMetaDto) => {
    const currentUpdatedAt = ctx.get(updatedAtAtom)
    const updatedAt = new Date(dto.updatedAt)
    if (DateF.isBefore(updatedAt, currentUpdatedAt)) {
      return
    }

    premiumIdAtom(ctx, dto.premiumId ? PremiumId(dto.premiumId) : undefined)
    statusAtom(ctx, dto.status)
    if (dto.banner) {
      bannerAtom(ctx, bannerFromDto(dto))
    }
    const {
      assets: { avatarsHost, tokenLogosHost, tokenBannersHost },
    } = ctx.get(environmentAtom)

    if (dto.archivedPremiums) {
      archivedPremiumsAtom(
        ctx,
        dto.archivedPremiums.map((dto) =>
          PremiumMeta.archivedPremiumFromDto(dto, {
            avatarsHost,
            tokenLogosHost,
            tokenBannersHost,
          }),
        ),
      )
    }
    if (dto.ownerDelugeId) {
      ownerAtom(ctx, PremiumOwner.fromDto(dto, avatarsHost))
    }

    premiumAtom(ctx, dto.premium)
    boostersAtom(ctx, dto.boosters)
    splashPointsAtom(ctx, dto.splashPoints ?? 0)
    tierAtom(ctx, tierFromDto(dto))
    expiresAtAtom.set(ctx, new Date(dto.expiresAt))
    trendingEmojiAtom(
      ctx,
      dto.trendingEmojiIds?.animated
        ? {
            emojiId: dto.trendingEmojiIds.animated,
          }
        : undefined,
    )
    purchaseTxHashAtom(ctx, dto.purchaseTxHash)
    socialsAtom(
      ctx,
      dto.socials ? Socials.fromDto(dto.socials) : Socials.empty(),
    )
    updatedAtAtom.set(ctx, new Date(dto.updatedAt))
  })

  const model = {
    premiumId: premiumIdAtom,
    currency,
    currencyAddress: currency.address,

    premium: premiumAtom,
    socials: socialsAtom,
    boosters: boostersAtom,
    splashPoints: splashPointsAtom,
    tier: tierAtom,
    expiresAt: expiresAtAtom,
    trendingEmoji: trendingEmojiAtom,
    purchaseTxHash: purchaseTxHashAtom,
    owner: ownerAtom,
    banner: bannerAtom,
    archivedPremiums: archivedPremiumsAtom,
    status: statusAtom,

    extensionEndDate: minExtensionDateAtom,
    isAllowedToExtend: isAllowedToExtendAtom,

    updateFromDto: updateFromDtoAction,
    updatedAt: updatedAtAtom,
  }

  Object.setPrototypeOf(model, PremiumMetaModelClass.prototype)

  return model
}

export interface PremiumMetaModel
  extends ReturnType<typeof reatomPremiumMeta> {}

const tierFromDto = (dto: ServicesApi.PremiumMetaDto) => {
  if (!dto.tier || dto.tier === 'no-tier') {
    return undefined
  }
  return dto.tier as PremiumTier
}

const bannerFromDto = (dto: ServicesApi.PremiumMetaDto) => {
  if (!dto.banner) {
    return undefined
  }
  if (dto.banner.startsWith('http://') || dto.banner.startsWith('https://')) {
    return dto.banner
  }
  return `${TOKEN_BANNERS_HOST}/${dto.banner}`
}
